import { GuideSection } from '@/components/guides/GuideSection';
import { GuideSteps, GuideStep } from '@/components/guides/GuideSteps';
import { GuideCallout } from '@/components/guides/GuideCallout';
import { GuideAction } from '@/components/guides/GuideAction';
import { GuideScreenshot } from '@/components/guides/GuideScreenshot';

const PRICING = [
  { label: 'Interview', rate: '$14.99 / setup', notes: 'Live 1:1 calls with real-time coaching.' },
  {
    label: 'Assessment',
    rate: '$7.99 / setup',
    notes: 'Solo work: tests, take-homes, timed drills.',
  },
];

const TIERS = [
  { range: '0 – 29 setups / mo', discount: '—', rate: '$14.99' },
  { range: '30 – 54 setups / mo', discount: '−10%', rate: '$13.49' },
  { range: '55+ setups / mo', discount: '−20%', rate: '$11.99' },
];

export function ReadBillingSection() {
  return (
    <GuideSection
      id="billing"
      eyebrow="Billing"
      title="Read your billing page"
      description="What every block on the Billing page means — plan, pricing, volume tiers, the itemized monthly statement, refunds — and how the trial credit works."
    >
      <GuideScreenshot
        src="/guides/billing-page.png"
        alt="Billing page showing current plan with quota bar, per-session pricing card with volume tier table, and empty refunds and invoices sections below"
        caption="The Billing page — plan at top, pricing rates and volume tiers in the middle, refund requests and past invoices at the bottom."
      />

      <GuideSteps>
        <GuideStep number={1} title="Plan card">
          <p>
            Shows your tier (for Wolf: <strong>Enterprise</strong>) and your quota for the month ({' '}
            <strong>200 interview setups</strong>). The progress bar fills with this month&apos;s{' '}
            <strong>billable setups</strong> against your monthly quota and turns amber past 90%
            usage so you can see a ceiling approaching. You are billed per confirmed interview or
            assessment setup — extra practice runs under the same setup are never billed again.
          </p>
          <GuideAction href="/billing">Open Billing</GuideAction>
        </GuideStep>

        <GuideStep number={2} title="Pricing card">
          <p>Two rows:</p>
          <ul className="ml-4 list-disc space-y-1">
            {PRICING.map((p) => (
              <li key={p.label}>
                <strong>{p.label}</strong> — {p.rate}. {p.notes}
              </li>
            ))}
          </ul>
          <p>
            Base rates are the Standard rates. Below them you see Effective rates that reflect any
            volume discount you currently qualify for.
          </p>
        </GuideStep>

        <GuideStep number={3} title="Volume tier table">
          <p>
            Discounts apply automatically once your monthly billable setup count crosses a
            threshold.
          </p>
          <div className="overflow-hidden rounded-xl border border-white/5">
            <table className="w-full text-xs">
              <thead className="bg-white/5 text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">Range</th>
                  <th className="px-3 py-2 text-left font-medium">Discount</th>
                  <th className="px-3 py-2 text-left font-medium">Interview rate</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5 text-white">
                {TIERS.map((t) => (
                  <tr key={t.range}>
                    <td className="px-3 py-2">{t.range}</td>
                    <td className="px-3 py-2 text-neurax-mint">{t.discount}</td>
                    <td className="px-3 py-2 font-mono">{t.rate}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p>
            The discount is retroactive for the month — hit 55 setups and <strong>all 55</strong>{' '}
            are billed at $11.99, not just the 55th and beyond.
          </p>
        </GuideStep>

        <GuideStep number={4} title="Monthly statement">
          <p>
            The heart of the page: an itemized, live-computed statement for any month. Use the month
            picker to jump to older billing periods. Every interview and assessment setup for the
            month is listed with candidate, position, type, and its charge —{' '}
            <strong>waived setups stay visible at $0.00</strong> with a Waived badge so the list
            always reconciles against your total setup count.
          </p>
          <p>
            The footer breaks the bill down completely: interviews, assessments, waived items,
            subtotal, any volume discount, and the total. Formal invoices, when issued, appear in
            the Invoices section below the statement.
          </p>
          <p>
            Think a charge shouldn't be there? Click <strong>Request waiver</strong> on that line,
            pick a reason (session never started, technical issue, duplicate, etc.), and F1Jobs
            reviews it within a business day — approved charges drop to $0 on the statement
            automatically.
          </p>
        </GuideStep>

        <GuideStep number={5} title="Refund & waiver requests list">
          <p>
            Every waiver/refund request you've submitted shows here with status (Pending, Approved,
            Rejected). F1Jobs reviews each one within a business day.
          </p>
        </GuideStep>
      </GuideSteps>

      <GuideCallout variant="success" title="$150 trial credit">
        <p>
          Your first <strong>$150 of interview setups</strong> — roughly <strong>10 setups</strong>{' '}
          at the Wolf rate — are on the house. After that, billing kicks in at the rates above. You
          don't need to do anything; F1Jobs waives the first qualifying setups automatically and
          they show as <em>Waived</em> $0.00 line items on your monthly statement.
        </p>
      </GuideCallout>

      <GuideCallout variant="info" title="How to track spend mid-month">
        <p>
          Open the monthly statement — the current month's total is computed live from your billable
          setups, with any volume discount already applied. No manual math needed.
        </p>
      </GuideCallout>

      <GuideCallout variant="warning" title="If something looks wrong on the statement">
        <p>
          Match each line item against the Interviews and Tests lists for the period. If a setup
          shows up that you don't recognise, flag it to F1Jobs support with the session ID — we'll
          investigate and waive or credit if warranted.
        </p>
      </GuideCallout>
    </GuideSection>
  );
}
